<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const enabled = ref(false);
const recipientUserIds = ref([]);
const intervalDays = ref(7);
const deliveryTime = ref('08:00');

const accountId = computed(() => Number(route.params.accountId));
const account = computed(
  () => store.getters['accounts/getAccount'](accountId.value) || {}
);
const uiFlags = computed(() => store.getters['accounts/getUIFlags']);
const users = computed(() =>
  store.getters['agents/getAgents'].filter(user => user.email)
);
const timezone = computed(
  () =>
    account.value.custom_attributes?.timezone ||
    account.value.settings?.reporting_timezone ||
    'UTC'
);
const lastSentAt = computed(
  () => account.value.settings?.automated_contacts_backup?.last_sent_at
);
const selectedRecipientLabels = computed(() =>
  recipientUserIds.value.map(id => {
    const user = users.value.find(item => item.id === id);
    return user ? `${user.name} — ${user.email}` : String(id);
  })
);
const recipientMenuItems = computed(() =>
  users.value
    .filter(user => !recipientUserIds.value.includes(user.id))
    .map(user => ({
      action: 'select',
      value: user.id,
      label: `${user.name} — ${user.email}`,
      thumbnail: {
        name: user.name,
        src: user.thumbnail || user.avatar_url || '',
      },
    }))
);
const isFormValid = computed(
  () =>
    !enabled.value ||
    (recipientUserIds.value.length > 0 &&
      intervalDays.value >= 1 &&
      intervalDays.value <= 365 &&
      /^([01]\d|2[0-3]):[0-5]\d$/.test(deliveryTime.value))
);

const setDefaults = currentAccount => {
  if (!currentAccount.id) return;

  const config = currentAccount.settings?.automated_contacts_backup || {};
  enabled.value = !!config.enabled;
  const configuredUserIds = (config.recipient_user_ids || []).map(Number);
  const legacyEmails = [
    ...(config.recipient_emails || []),
    config.recipient_email,
  ]
    .filter(Boolean)
    .map(email => email.toLowerCase());

  recipientUserIds.value = configuredUserIds.length
    ? configuredUserIds
    : users.value
        .filter(user => legacyEmails.includes(user.email.toLowerCase()))
        .map(user => user.id);

  if (!recipientUserIds.value.length && users.value.length) {
    const currentUserId = store.getters.getCurrentUser?.id;
    const defaultUser = users.value.find(user => user.id === currentUserId);
    recipientUserIds.value = defaultUser ? [defaultUser.id] : [];
  }
  intervalDays.value = config.interval_days || 7;
  deliveryTime.value = config.delivery_time || '08:00';
};

watch([account, users], ([currentAccount]) => setDefaults(currentAccount), {
  immediate: true,
});

const handleRecipientAdd = ({ value }) => {
  const userId = Number(value);
  if (!recipientUserIds.value.includes(userId)) {
    recipientUserIds.value.push(userId);
  }
};

const handleRecipientRemove = index => {
  recipientUserIds.value.splice(index, 1);
};

const saveSettings = async () => {
  if (!isFormValid.value) {
    useAlert(t('AUTOMATED_EMAILS.FORM.INVALID'));
    return;
  }

  try {
    await store.dispatch('accounts/update', {
      automated_contacts_backup: {
        enabled: enabled.value,
        recipient_user_ids: recipientUserIds.value,
        interval_days: Number(intervalDays.value),
        delivery_time: deliveryTime.value,
      },
    });
    useAlert(t('AUTOMATED_EMAILS.FORM.SUCCESS'));
  } catch {
    useAlert(t('AUTOMATED_EMAILS.FORM.ERROR'));
  }
};

onMounted(() => store.dispatch('agents/get'));
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader
      :title="$t('AUTOMATED_EMAILS.TITLE')"
      :description="$t('AUTOMATED_EMAILS.DESCRIPTION')"
    />
    <SectionLayout
      :title="$t('AUTOMATED_EMAILS.CONTACTS_BACKUP.TITLE')"
      :description="$t('AUTOMATED_EMAILS.CONTACTS_BACKUP.DESCRIPTION')"
      class="!pt-3"
    >
      <form class="grid gap-5" @submit.prevent="saveSettings">
        <div class="flex items-center justify-between gap-4">
          <div class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AUTOMATED_EMAILS.FORM.ENABLED.LABEL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('AUTOMATED_EMAILS.FORM.ENABLED.HELP') }}
            </span>
          </div>
          <Switch v-model="enabled" />
        </div>

        <WithLabel
          name="backup-recipient"
          :label="$t('AUTOMATED_EMAILS.FORM.RECIPIENT.LABEL')"
        >
          <div
            class="rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong px-2 py-2"
          >
            <TagInput
              :model-value="selectedRecipientLabels"
              :menu-items="recipientMenuItems"
              :placeholder="$t('AUTOMATED_EMAILS.FORM.RECIPIENT.PLACEHOLDER')"
              show-dropdown
              skip-label-dedup
              :auto-open-dropdown="false"
              @add="handleRecipientAdd"
              @remove="handleRecipientRemove"
            />
          </div>
          <p class="mt-1 mb-0 text-xs text-n-slate-10">
            {{ $t('AUTOMATED_EMAILS.FORM.RECIPIENT.HELP') }}
          </p>
        </WithLabel>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <WithLabel
            name="backup-interval"
            :label="$t('AUTOMATED_EMAILS.FORM.INTERVAL.LABEL')"
          >
            <Input
              v-model="intervalDays"
              type="number"
              min="1"
              max="365"
              class="w-full"
            />
          </WithLabel>
          <WithLabel
            name="backup-time"
            :label="$t('AUTOMATED_EMAILS.FORM.TIME.LABEL')"
          >
            <Input v-model="deliveryTime" type="time" class="w-full" />
          </WithLabel>
        </div>

        <p class="text-xs text-n-slate-11">
          {{ $t('AUTOMATED_EMAILS.FORM.TIMEZONE', { timezone }) }}
        </p>
        <p v-if="lastSentAt" class="text-xs text-n-slate-11">
          {{
            $t('AUTOMATED_EMAILS.FORM.LAST_SENT', {
              date: new Date(lastSentAt).toLocaleString(),
            })
          }}
        </p>

        <div>
          <Button
            type="submit"
            :label="$t('AUTOMATED_EMAILS.FORM.SAVE')"
            :disabled="!isFormValid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </form>
    </SectionLayout>
  </div>
</template>
